// ============================================
// MedGemma Comparison Engine
// Clinical Calibration Module — Phase 2
// ============================================
//
// Runs after Dr. C submits Phase 1 blind diagnosis.
// Extracts structured decision points from free-text,
// compares against MedGemma's assessment, and computes
// alignment scores with clinical weighting.
// ============================================

import type {
  DecisionPoint,
  FlaggedLab,
  MedGemmaAssessment,
} from '../../types/clinicalCalibration';
import { DECISION_AREAS } from '../../types/clinicalCalibration';

// ---- Constants ----

import { CF_URLS } from '../../config/api';

export const CLAUDE_PROXY_URL = CF_URLS.claudeProxy;

// ---- Internal helpers ----

/**
 * Extract JSON from a Claude response string.
 * Handles both fenced ```json blocks and raw JSON arrays.
 */
function extractJSON<T>(text: string): T {
  // Try fenced code block first
  const fenced = text.match(/```json\s*([\s\S]*?)```/);
  if (fenced) {
    return JSON.parse(fenced[1].trim()) as T;
  }

  // Try raw JSON (find first [ or { )
  const start = text.search(/[\[{]/);
  if (start !== -1) {
    // Walk forward to find the matching closing bracket
    const open = text[start];
    const close = open === '[' ? ']' : '}';
    let depth = 0;
    for (let i = start; i < text.length; i++) {
      if (text[i] === open) depth++;
      if (text[i] === close) depth--;
      if (depth === 0) {
        return JSON.parse(text.slice(start, i + 1)) as T;
      }
    }
  }

  throw new Error('Could not extract JSON from Claude response');
}

/** Build the system prompt for the comparison extraction call. */
function buildSystemPrompt(): string {
  return `You are a clinical calibration engine for a functional medicine platform. Your job is to compare a physician's blind diagnosis against a MedGemma AI assessment and produce structured decision point comparisons.

You MUST respond with ONLY a JSON array — no explanation, no markdown outside the JSON fence. Use this exact format:

\`\`\`json
[
  {
    "area": "Primary Dx",
    "drCPosition": "...",
    "medgemmaPosition": "...",
    "alignment": "aligned" | "delta" | "both_missed",
    "deltaNote": "..."
  },
  ...
]
\`\`\`

Rules for determining alignment:
- "aligned": Both identified substantially the same finding, even if worded differently.
- "delta": One identified something the other missed, or they disagree on interpretation.
- "both_missed": Neither addressed an area that clinical evidence suggests should have been caught given the labs/transcript.

For deltaNote:
- If aligned, set deltaNote to null.
- If delta, explain the specific divergence concisely (1-2 sentences).
- If both_missed, explain what was missed and why it matters.

Be clinically precise. Do not inflate alignment — only mark "aligned" when positions genuinely converge.`;
}

/** Build the user prompt with all Phase 1 inputs and MedGemma assessment. */
function buildUserPrompt(
  drCDiagnosis: string,
  drCFlaggedLabs: FlaggedLab[],
  drCProducts: string[],
  drCLabOrders: string[],
  drCChatNotes: string[],
  medgemmaAssessment: MedGemmaAssessment,
): string {
  const areasDescription = DECISION_AREAS.map(
    (da) => `${da.area} (weight: ${da.clinicalWeight})`,
  ).join('\n  ');

  return `Compare the physician's blind diagnosis against MedGemma's assessment across the 9 standard decision areas.

== PHYSICIAN (Dr. C) INPUTS ==

Diagnosis (free-text):
${drCDiagnosis}

Flagged Labs:
${drCFlaggedLabs.length > 0
    ? drCFlaggedLabs
        .map(
          (l) =>
            `- ${l.marker}: ${l.value} ${l.unit} (std range: ${l.stdRange}${l.functionalRange ? `, functional range: ${l.functionalRange}` : ''}${l.flagReason ? `, reason: ${l.flagReason}` : ''})`,
        )
        .join('\n')
    : '(none flagged)'}

Selected Products:
${drCProducts.length > 0 ? drCProducts.map((p) => `- ${p}`).join('\n') : '(none selected)'}

Ordered Lab Panels:
${drCLabOrders.length > 0 ? drCLabOrders.map((l) => `- ${l}`).join('\n') : '(none ordered)'}

Chat Notes (from transcript review):
${drCChatNotes.length > 0 ? drCChatNotes.map((n) => `- ${n}`).join('\n') : '(none)'}

== MEDGEMMA ASSESSMENT ==

Diagnosis: ${medgemmaAssessment.diagnosis}
Protocol: ${medgemmaAssessment.protocol}
Evidence Grade: ${medgemmaAssessment.grade}
Matched Protocols: ${medgemmaAssessment.matchedProtocols.join(', ') || '(none)'}
Recommended Products: ${medgemmaAssessment.recommendedProducts.join(', ') || '(none)'}
Recommended Lab Orders: ${medgemmaAssessment.recommendedLabOrders.join(', ') || '(none)'}
Monthly Cost: $${medgemmaAssessment.totalMonthlyCost.toFixed(2)}
${medgemmaAssessment.escalationPath ? `Escalation Path: ${medgemmaAssessment.escalationPath}` : ''}

== DECISION AREAS TO EVALUATE ==

  ${areasDescription}

For each of the 9 areas, extract what Dr. C's position is (from the inputs above), what MedGemma's position is, and whether they are aligned, divergent (delta), or both missed something.

For "Primary Dx": Compare root cause identification between the two.
For "Key Lab Read": Compare which markers were flagged and how they were interpreted.
For "Secondary Findings": Compare additional deficiencies or conditions caught beyond primary dx.
For "Supplement-First": Assess whether each followed Gate 1 (supplements) before Gate 2 (Rx) protocol adherence.
For "Product Selection": Compare which products/bundles were selected.
For "Escalation Path": Compare when/whether each recommended provider consult escalation.
For "Missed Signals": Identify symptoms or data in the case that neither party caught.
For "Cost Awareness": Evaluate protocol cost awareness and patient cost sensitivity considerations.
For "Evidence Grade": Compare strength of clinical evidence backing each position.

Return ONLY the JSON array with exactly 9 objects, one per area, in the order listed above.`;
}

// ---- Exported functions ----

/**
 * Extract structured decision points by sending Dr. C's inputs and
 * MedGemma's assessment to Claude for comparative analysis.
 */
export async function extractDecisionPoints(
  drCDiagnosis: string,
  drCFlaggedLabs: FlaggedLab[],
  drCProducts: string[],
  drCLabOrders: string[],
  drCChatNotes: string[],
  medgemmaAssessment: MedGemmaAssessment,
): Promise<DecisionPoint[]> {
  const systemPrompt = buildSystemPrompt();
  const userPrompt = buildUserPrompt(
    drCDiagnosis,
    drCFlaggedLabs,
    drCProducts,
    drCLabOrders,
    drCChatNotes,
    medgemmaAssessment,
  );

  const response = await fetch(CLAUDE_PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-5-20250929',
      max_tokens: 2000,
      messages: [{ role: 'user', content: userPrompt }],
      system: systemPrompt,
    }),
  });

  if (!response.ok) {
    const errText = await response.text();
    throw new Error(
      `Claude proxy returned ${response.status}: ${errText.slice(0, 200)}`,
    );
  }

  const data = await response.json();
  const rawText: string = data.content?.[0]?.text ?? '';

  if (!rawText) {
    throw new Error('Claude proxy returned empty content');
  }

  // Parse the JSON response
  const rawPoints = extractJSON<
    {
      area: string;
      drCPosition: string;
      medgemmaPosition: string;
      alignment: 'aligned' | 'delta' | 'both_missed';
      deltaNote?: string | null;
    }[]
  >(rawText);

  if (!Array.isArray(rawPoints) || rawPoints.length === 0) {
    throw new Error('Claude response did not contain a valid decision points array');
  }

  // Map parsed points to typed DecisionPoints, merging in clinical weights
  // from the canonical DECISION_AREAS list.
  const weightMap = new Map(
    DECISION_AREAS.map((da) => [da.area, da.clinicalWeight]),
  );

  const decisionPoints: DecisionPoint[] = rawPoints.map((rp) => {
    const weight = weightMap.get(rp.area);
    if (weight === undefined) {
      console.warn(
        `[ComparisonEngine] Unknown decision area "${rp.area}" returned by Claude — defaulting weight to 0`,
      );
    }

    return {
      area: rp.area,
      drCPosition: rp.drCPosition,
      medgemmaPosition: rp.medgemmaPosition,
      alignment: rp.alignment,
      deltaNote: rp.deltaNote ?? undefined,
      clinicalWeight: weight ?? 0,
    };
  });

  // Ensure all 9 canonical areas are present. If Claude omitted any,
  // insert a placeholder with both_missed alignment.
  for (const da of DECISION_AREAS) {
    if (!decisionPoints.find((dp) => dp.area === da.area)) {
      console.warn(
        `[ComparisonEngine] Claude omitted "${da.area}" — inserting placeholder`,
      );
      decisionPoints.push({
        area: da.area,
        drCPosition: 'Not assessed',
        medgemmaPosition: 'Not assessed',
        alignment: 'both_missed',
        deltaNote: 'This area was not evaluated in the comparison.',
        clinicalWeight: da.clinicalWeight,
      });
    }
  }

  return decisionPoints;
}

/**
 * Compute weighted alignment score (0-100).
 *
 * Each aligned decision point contributes its full clinical weight.
 * Delta and both_missed contribute zero.
 */
export function computeAlignmentScore(decisionPoints: DecisionPoint[]): number {
  const score = decisionPoints.reduce(
    (sum, dp) =>
      sum + (dp.alignment === 'aligned' ? dp.clinicalWeight * 100 : 0),
    0,
  );
  // Clamp to 0-100 in case of floating-point drift
  return Math.round(Math.min(100, Math.max(0, score)));
}

/**
 * Generate a comparison summary with counts and a one-line overview.
 */
export function generateComparisonSummary(
  decisionPoints: DecisionPoint[],
): {
  aligned: number;
  deltas: number;
  bothMissed: number;
  summary: string;
} {
  let aligned = 0;
  let deltas = 0;
  let bothMissed = 0;

  for (const dp of decisionPoints) {
    switch (dp.alignment) {
      case 'aligned':
        aligned++;
        break;
      case 'delta':
        deltas++;
        break;
      case 'both_missed':
        bothMissed++;
        break;
    }
  }

  const total = decisionPoints.length;
  const parts: string[] = [];

  parts.push(`${aligned}/${total} decision points aligned.`);

  if (deltas > 0) {
    parts.push(`${deltas} divergence${deltas > 1 ? 's' : ''} detected.`);
  }

  if (bothMissed > 0) {
    parts.push(`${bothMissed} area${bothMissed > 1 ? 's' : ''} missed by both.`);
  }

  return {
    aligned,
    deltas,
    bothMissed,
    summary: parts.join(' '),
  };
}
